import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { QRTypeSelector } from '@/components/qr/QRTypeSelector';
import { QRContentForm } from '@/components/qr/QRContentForm';
import { DynamicToggle } from '@/components/qr/DynamicToggle';
import { DesignTabs } from '@/components/qr/design/DesignTabs';
import { QRPreview } from '@/components/qr/QRPreview';
import { RoutingRulesEditor } from '@/components/qr/RoutingRulesEditor';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Palette, Sparkles } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useQRStore, QRType } from '@/stores/qrStore';
import { useQRCodes } from '@/hooks/useQRCodes';
import { useSearchParams } from 'react-router-dom';
import { toast } from '@/components/ui/sonner';

export default function Create() {
  const [activeMainTab, setActiveMainTab] = useState('content');
  const { currentQR, setCurrentQR } = useQRStore();
  const { dynamicCount } = useQRCodes();
  const [searchParams] = useSearchParams();

  // Handle upgrade from scanned QR
  useEffect(() => {
    const isUpgrade = searchParams.get('upgrade') === 'true';
    const typeParam = searchParams.get('type') as QRType | null;

    // Check for pending QR download after authentication
    const pendingDownload = localStorage.getItem('pending_qr_download');
    if (pendingDownload) {
      try {
        const { qr, timestamp } = JSON.parse(pendingDownload);
        // Only restore if less than 1 hour old
        if (Date.now() - timestamp < 3600000) {
          setCurrentQR(qr);
          toast.success('Your QR design has been restored. You can now download it!', { icon: '✨' });
        }
        localStorage.removeItem('pending_qr_download');
      } catch (error) {
        console.error('Failed to restore pending download:', error);
        localStorage.removeItem('pending_qr_download');
      }
      return;
    }

    if (isUpgrade) {
      const storedData = localStorage.getItem('scanned_qr_upgrade');
      
      if (storedData) {
        try {
          const { type, rawContent, parsedData } = JSON.parse(storedData);
          
          // Set QR type if provided in URL
          if (typeParam) {
            setCurrentQR({ type: typeParam });
          }

          // Pre-fill based on QR type
          setTimeout(() => {
            switch (type) {
              case 'url':
                setCurrentQR({ 
                  type: 'url',
                  contentData: { url: rawContent }, 
                  isDynamic: true 
                });
                toast.success('Scanned URL loaded! Now make it dynamic & trackable', { icon: '✨' });
                break;

              case 'vcard':
                if (parsedData) {
                  setCurrentQR({
                    type: 'vcard',
                    contentData: {
                      name: parsedData.name || '',
                      phone: parsedData.phone || '',
                      email: parsedData.email || '',
                      organization: parsedData.organization || '',
                      title: parsedData.title || '',
                      address: parsedData.address || '',
                      website: parsedData.website || '',
                    },
                    isDynamic: true,
                  });
                  toast.success('Contact info loaded! Upgrade to dynamic vCard', { icon: '✨' });
                }
                break;

              case 'wifi':
                if (parsedData) {
                  setCurrentQR({
                    type: 'wifi',
                    contentData: {
                      ssid: parsedData.ssid || '',
                      password: parsedData.password || '',
                      encryption: parsedData.encryption || 'WPA',
                    },
                  });
                  toast.success('WiFi credentials loaded!', { icon: '✨' });
                }
                break;

              case 'email':
                if (parsedData) {
                  setCurrentQR({
                    type: 'email',
                    contentData: {
                      email: parsedData.email || '',
                      subject: parsedData.subject || '',
                      body: parsedData.body || '',
                    },
                    isDynamic: true,
                  });
                  toast.success('Email template loaded!', { icon: '✨' });
                }
                break;

              case 'sms':
                if (parsedData) {
                  setCurrentQR({
                    type: 'sms',
                    contentData: {
                      phone: parsedData.phone || '',
                      message: parsedData.message || '',
                    },
                  });
                  toast.success('SMS loaded! Make it dynamic', { icon: '✨' });
                }
                break;

              case 'phone':
                setCurrentQR({ 
                  type: 'phone',
                  contentData: { phone: rawContent.replace(/^tel:/, '') } 
                });
                toast.success('Phone number loaded!', { icon: '✨' });
                break;

              case 'geo':
                if (parsedData) {
                  setCurrentQR({
                    type: 'location',
                    contentData: {
                      latitude: parsedData.latitude || '',
                      longitude: parsedData.longitude || '',
                    },
                    isDynamic: true,
                  });
                  toast.success('Location loaded! Add geo-targeting', { icon: '✨' });
                }
                break;

              case 'event':
                if (parsedData) {
                  setCurrentQR({
                    type: 'calendar',
                    contentData: {
                      title: parsedData.title || '',
                      location: parsedData.location || '',
                      startDate: parsedData.start || '',
                      endDate: parsedData.end || '',
                      description: parsedData.description || '',
                    },
                  });
                  toast.success('Event loaded! Make it dynamic', { icon: '✨' });
                }
                break;

              case 'text':
                setCurrentQR({ 
                  type: 'text',
                  contentData: { text: rawContent } 
                });
                toast.success('Text loaded!', { icon: '✨' });
                break;

              default:
                setCurrentQR({ 
                  type: 'url',
                  contentData: { url: rawContent }, 
                  isDynamic: true 
                });
                toast.success('QR data loaded!', { icon: '✨' });
            }
          }, 100);

          // Clear the stored data after using it
          localStorage.removeItem('scanned_qr_upgrade');
        } catch (error) {
          console.error('Failed to parse scanned data:', error);
        }
      }
    }
  }, [searchParams, setCurrentQR]);

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-2xl md:text-3xl font-black tracking-tight mb-1">Create QR Code</h1>
          <p className="text-sm text-muted-foreground">
            Choose a type, customize your design, and download
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] xl:grid-cols-[1fr_420px] gap-6 lg:gap-8">
          {/* Left side - Forms with Content/Design tabs */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full">
              <TabsList className="w-full grid grid-cols-2 h-11 mb-5 bg-muted/50 rounded-xl p-1">
                <TabsTrigger value="content" className="flex items-center gap-1.5 text-[13px] font-semibold rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm">
                  <FileText className="h-3.5 w-3.5" />
                  Content
                </TabsTrigger>
                <TabsTrigger value="design" className="flex items-center gap-1.5 text-[13px] font-semibold rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm">
                  <Palette className="h-3.5 w-3.5" />
                  Design
                </TabsTrigger>
              </TabsList>

              <TabsContent value="content" className="mt-0 space-y-4">
                <div className="bg-card border border-border/50 rounded-xl p-5">
                  <DynamicToggle
                    isDynamic={currentQR.isDynamic}
                    onChange={(isDynamic) => setCurrentQR({ isDynamic })}
                  />
                </div>

                <div className="bg-card border border-border/50 rounded-xl p-5">
                  <QRTypeSelector />
                </div>

                <div className="bg-card border border-border/50 rounded-xl p-5">
                  <QRContentForm />
                </div>

                {currentQR.isDynamic && (
                  <div className="bg-card border border-border/50 rounded-xl p-5">
                    <RoutingRulesEditor
                      rules={currentQR.routingRules || []}
                      onChange={(rules) => setCurrentQR({ routingRules: rules })}
                    />
                  </div>
                )}
              </TabsContent>

              <TabsContent value="design" className="mt-0">
                <div className="bg-card border border-border/50 rounded-xl p-5">
                  <DesignTabs />
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>

          {/* Right side - Preview */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:sticky lg:top-24 lg:h-fit"
          >
            <div className="bg-card border border-border/50 rounded-xl p-5">
              <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest mb-4">
                Live Preview
              </h3>
              <QRPreview />
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
